import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { setSupervisorRateLimitError } from "../../../features/user/user-slice";
import axios from "../../../utils/api-client";
import type { HolidaysState } from "../types";
import { Holiday, HolidayPayload, HolidayScope, HolidayType } from "../types";

const API = process.env.REACT_APP_API_END_POINT;

const initialAsyncState = { status: "idle" as const, errorMessage: "" };

const initialState: HolidaysState = {
	holidayTypes: { ...initialAsyncState, data: [] },
	companyHolidays: { ...initialAsyncState, data: [] },
	locationHolidays: {},
	createHoliday: { ...initialAsyncState },
	editHoliday: { ...initialAsyncState },
	deleteHoliday: { ...initialAsyncState },
};

interface ThunkErrorApi {
	dispatch: (action: unknown) => unknown;
	rejectWithValue: (value: unknown) => unknown;
}

// Server business-rule failures carry `{ message }`; fall back to the
// generic message in the reducer when there is none.
const rejectWithApiError = (error: any, { dispatch, rejectWithValue }: ThunkErrorApi) => {
	const status = error?.response?.status;
	if (status === 429) {
		dispatch(setSupervisorRateLimitError());
		return rejectWithValue({ status, message: "" });
	}
	return rejectWithValue({
		status,
		message: error?.response?.data?.message ?? "",
	});
};

const getErrorMessage = (payload: unknown): string =>
	(payload as { message?: string } | undefined)?.message ?? "";

export const getHolidayTypes = createAsyncThunk(
	"holidays/get-holiday-types",
	async (_: void, { fulfillWithValue, rejectWithValue, dispatch }) => {
		try {
			const res = await axios.get(`${API}/holiday-types`);
			return fulfillWithValue((res.data ?? []) as HolidayType[]);
		} catch (error: any) {
			return rejectWithApiError(error, { dispatch, rejectWithValue });
		}
	}
);

export const getCompanyHolidays = createAsyncThunk(
	"holidays/get-company-holidays",
	async (companyId: number, { fulfillWithValue, rejectWithValue, dispatch }) => {
		try {
			const res = await axios.get(`${API}/companies/${companyId}/holidays`);
			return fulfillWithValue((res.data ?? []) as Holiday[]);
		} catch (error: any) {
			return rejectWithApiError(error, { dispatch, rejectWithValue });
		}
	}
);

export const getLocationHolidays = createAsyncThunk(
	"holidays/get-location-holidays",
	async (locationId: number, { fulfillWithValue, rejectWithValue, dispatch }) => {
		try {
			const res = await axios.get(`${API}/locations/${locationId}/holidays`);
			return fulfillWithValue((res.data ?? []) as Holiday[]);
		} catch (error: any) {
			return rejectWithApiError(error, { dispatch, rejectWithValue });
		}
	}
);

interface HolidayTarget {
	companyId: number;
	// Set when the mutation is made from a Location's Holidays section
	locationId?: number;
}

// Company-level holidays go through the company route. A location's own
// additions go through the location route (the only one LocationAdmins can
// use); admins editing a company row from the location screen use the
// company route, which the API permits.
const getHolidaysUrl = (
	scope: HolidayScope,
	{ companyId, locationId }: HolidayTarget
): string =>
	scope === "location" && locationId
		? `${API}/locations/${locationId}/holidays`
		: `${API}/companies/${companyId}/holidays`;

// Keep whichever list the caller is looking at in sync, and drop every other
// cached location list — a company holiday change affects all of them.
const refreshHolidayLists = (
	dispatch: (action: unknown) => unknown,
	{ companyId, locationId }: HolidayTarget
) => {
	dispatch(invalidateLocationHolidays());
	if (locationId) dispatch(getLocationHolidays(locationId));
	else dispatch(getCompanyHolidays(companyId));
};

export const createHoliday = createAsyncThunk(
	"holidays/create-holiday",
	async (
		{
			scope,
			target,
			holiday,
		}: { scope: HolidayScope; target: HolidayTarget; holiday: HolidayPayload },
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const res = await axios.post(getHolidaysUrl(scope, target), holiday);
			refreshHolidayLists(dispatch, target);
			return fulfillWithValue(res.data as { id: number });
		} catch (error: any) {
			return rejectWithApiError(error, { dispatch, rejectWithValue });
		}
	}
);

export const editHoliday = createAsyncThunk(
	"holidays/edit-holiday",
	async (
		{
			id,
			scope,
			target,
			holiday,
		}: {
			id: number;
			scope: HolidayScope;
			target: HolidayTarget;
			holiday: HolidayPayload;
		},
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			await axios.put(`${getHolidaysUrl(scope, target)}/${id}`, holiday);
			refreshHolidayLists(dispatch, target);
			return fulfillWithValue(id);
		} catch (error: any) {
			return rejectWithApiError(error, { dispatch, rejectWithValue });
		}
	}
);

export const deleteHoliday = createAsyncThunk(
	"holidays/delete-holiday",
	async (
		{
			id,
			scope,
			target,
		}: { id: number; scope: HolidayScope; target: HolidayTarget },
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			await axios.delete(`${getHolidaysUrl(scope, target)}/${id}`);
			refreshHolidayLists(dispatch, target);
			return fulfillWithValue(id);
		} catch (error: any) {
			return rejectWithApiError(error, { dispatch, rejectWithValue });
		}
	}
);

const HolidaysSlice = createSlice({
	name: "holidays",
	initialState,
	reducers: {
		invalidateLocationHolidays: (state) => {
			state.locationHolidays = {};
		},
		resetCreateHoliday: (state) => {
			state.createHoliday = { ...initialAsyncState };
		},
		resetEditHoliday: (state) => {
			state.editHoliday = { ...initialAsyncState };
		},
		resetDeleteHoliday: (state) => {
			state.deleteHoliday = { ...initialAsyncState };
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getHolidayTypes.pending, (state) => {
			state.holidayTypes.status = "pending";
		});
		builder.addCase(getHolidayTypes.fulfilled, (state, action) => {
			state.holidayTypes.status = "fulfilled";
			state.holidayTypes.data = action.payload;
		});
		builder.addCase(getHolidayTypes.rejected, (state, action) => {
			state.holidayTypes.status = "error";
			state.holidayTypes.errorMessage = getErrorMessage(action.payload);
		});

		builder.addCase(getCompanyHolidays.pending, (state) => {
			state.companyHolidays.status = "pending";
		});
		builder.addCase(getCompanyHolidays.fulfilled, (state, action) => {
			state.companyHolidays.status = "fulfilled";
			state.companyHolidays.data = action.payload;
		});
		builder.addCase(getCompanyHolidays.rejected, (state, action) => {
			state.companyHolidays.status = "error";
			state.companyHolidays.errorMessage = getErrorMessage(action.payload);
		});

		builder.addCase(getLocationHolidays.pending, (state, action) => {
			const locationId = action.meta.arg;
			state.locationHolidays[locationId] = {
				status: "pending",
				errorMessage: "",
				data: state.locationHolidays[locationId]?.data ?? [],
			};
		});
		builder.addCase(getLocationHolidays.fulfilled, (state, action) => {
			state.locationHolidays[action.meta.arg] = {
				status: "fulfilled",
				errorMessage: "",
				data: action.payload,
			};
		});
		builder.addCase(getLocationHolidays.rejected, (state, action) => {
			const locationId = action.meta.arg;
			state.locationHolidays[locationId] = {
				status: "error",
				errorMessage: getErrorMessage(action.payload),
				data: state.locationHolidays[locationId]?.data ?? [],
			};
		});

		builder.addCase(createHoliday.pending, (state) => {
			state.createHoliday = { status: "pending", errorMessage: "" };
		});
		builder.addCase(createHoliday.fulfilled, (state) => {
			state.createHoliday.status = "fulfilled";
		});
		builder.addCase(createHoliday.rejected, (state, action) => {
			state.createHoliday.status = "error";
			state.createHoliday.errorMessage = getErrorMessage(action.payload);
		});

		builder.addCase(editHoliday.pending, (state) => {
			state.editHoliday = { status: "pending", errorMessage: "" };
		});
		builder.addCase(editHoliday.fulfilled, (state) => {
			state.editHoliday.status = "fulfilled";
		});
		builder.addCase(editHoliday.rejected, (state, action) => {
			state.editHoliday.status = "error";
			state.editHoliday.errorMessage = getErrorMessage(action.payload);
		});

		builder.addCase(deleteHoliday.pending, (state) => {
			state.deleteHoliday = { status: "pending", errorMessage: "" };
		});
		builder.addCase(deleteHoliday.fulfilled, (state) => {
			state.deleteHoliday.status = "fulfilled";
		});
		builder.addCase(deleteHoliday.rejected, (state, action) => {
			state.deleteHoliday.status = "error";
			state.deleteHoliday.errorMessage = getErrorMessage(action.payload);
		});
	},
});

export const {
	invalidateLocationHolidays,
	resetCreateHoliday,
	resetEditHoliday,
	resetDeleteHoliday,
} = HolidaysSlice.actions;

export default HolidaysSlice.reducer;
